import imageBuilder, { CustomImageUrlBuilder } from "../components/sanity/imageBuilder";
import {
  NullCheckedSanityImage,
  SanityImageSourceExt
} from "../models/shared/SanityImageSourceExt";
import { isNullOrWhiteSpace } from "../shared/string";
import { Breakpoint } from "./breakpoints";

export interface ImagePreset {
  height?: number;
  width?: number;
}

export type ImagePresetCollection = Partial<Record<Breakpoint, ImagePreset>> & {
  "default": ImagePreset;
};

export interface ImageSource {
  minimumBreakpoint: number;
  srcSet: string;
  preset: ImagePreset;
}

export function getImageAltText(image: SanityImageSourceExt) {
  if (!isNullOrWhiteSpace(image?.alt)) {
    return image.alt;
  }

  if (!isNullOrWhiteSpace(image?.asset?.altText)) {
    return image.asset!.altText;
  }

  if (!isNullOrWhiteSpace(image?.asset?.title)) {
    const altText = image.asset!.title.replace(/[-_]/g, " "); // Replace dashes and underscores with spaces
    return altText.charAt(0).toUpperCase() + altText.slice(1);
  }
  return undefined;
}

/**
 * Validate that image has an asset and that it's not a fileAsset (pdf, mp4, etc.)
 * to avoid {@link imageBuilder} hanging. Always run this before passing an image to
 * an {@link imageBuilder} or any function that takes {@link NullCheckedSanityImage}.
 * @param image Image to validate (is treated as {@link NullCheckedSanityImage} if valid)
 * @returns true if the image has an asset and is an imageAsset
 */
export const validImage = (
  image: SanityImageSourceExt | null | undefined
): image is NullCheckedSanityImage =>
  !!image?.asset && image.asset._type !== "sanity.fileAsset";

export function getImagePresetUrl(image: SanityImageSourceExt, preset: ImagePreset) {
  if (!validImage(image)) {
    return null;
  }

  let builder: CustomImageUrlBuilder | undefined;

  if (preset.width && preset.height) {
    builder = imageBuilder(image).size(preset.width, preset.height);
  } else if (preset.width) {
    builder = imageBuilder(image).width(preset.width);
  } else if (preset.height) {
    builder = imageBuilder(image).height(preset.height);
  }

  return builder?.auto("format").url({ friendlyFilename: true });
}
